import React, { useEffect, useRef, useState } from 'react';
import Modal from '../Modal';
import HelpReader, { Marked } from './HelpReader';
import { helpApi } from '../../lib/help';
import './Help.css';

/**
 * "Try these first" (Help, 123): as a ticket's title is typed on Create
 * Ticket, up to three published Help articles that might answer it - from
 * Help's own search, among what this person may read (GET /help/suggest).
 *
 *   - asked 400 ms after the typing stops, and only from three characters;
 *   - only the answer to the latest title is ever shown (a request counter
 *     drops a slower, older one arriving after it);
 *   - an article opens in a dialog over the form, so nothing typed is lost,
 *     and `onOpened(id)` tells the form it was opened - which is what makes
 *     leaving without a ticket count as "solved without a ticket";
 *   - it never stands in the way: no answer, a slow answer or a failed one
 *     is simply no box, and the form submits whatever this is doing.
 */
const WAIT_MS = 400;

export default function HelpSuggestions({ title, onOpened, enabled = true }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);     // { id, title }
  const seq = useRef(0);
  const q = String(title || '').trim();

  useEffect(() => {
    const n = ++seq.current;
    if (!enabled || q.length < 3) { setList([]); return undefined; }
    const t = setTimeout(() => {
      helpApi.get('/help/suggest', { q })
        .then((r) => { if (n === seq.current) setList(Array.isArray(r?.results) ? r.results.slice(0, 3) : []); })
        .catch(() => { if (n === seq.current) setList([]); });
    }, WAIT_MS);
    return () => clearTimeout(t);
  }, [q, enabled]);

  if (!list.length && !open) return null;
  return (
    <>
      {list.length > 0 && (
        <aside className="hp-sugg" aria-label="Try these first" data-testid="help-suggestions">
          <p className="hp-sugg-title">Try these first</p>
          <ul className="hp-sugg-list">
            {list.map((a) => (
              <li key={a.id}>
                <button type="button" className="hp-sugg-item" onClick={() => setOpen({ id: a.id, title: a.title })}>
                  <span className="hp-sugg-name">{a.title}</span>
                  {a.snippet && <span className="hp-sugg-snip"><Marked text={a.snippet} /></span>}
                </button>
              </li>
            ))}
          </ul>
          <p className="hp-sugg-foot">From Help. You can still raise the ticket.</p>
        </aside>
      )}
      {open && (
        <Modal wide title={open.title} subtitle="From Help" onClose={() => setOpen(null)}
               footer={<button type="button" className="btn btn-primary" onClick={() => setOpen(null)}>Back to my ticket</button>}>
          <HelpReader id={open.id} compact onOpen={onOpened} />
        </Modal>
      )}
    </>
  );
}
